/**
 * AdminGeoContentDetail.jsx — GEO İçerik Onay/Yayın Detayı + Düzenleme Modu
 *
 * Google "scaled content abuse" cezasına karşı ANA SAVUNMA: bu ekran, admin'in
 * otomasyonun ürettiği bir makaleyi TAM OKUYUP tek tek onaylamasını/reddetmesini
 * sağlar. Toplu onay YOK (bilinçli — proje-yoneticisi görev talimatı).
 *
 * ⛔ GÜVENLİK — Yayın Görünümü (view mode): Önizleme backend'in ürettiği
 * `body_html`'i (services/geo_render.py::render_article_html — bleach
 * whitelist + href şema allowlist ile sanitize edilmiş) `sanitizeHtmlPreview`
 * (utils/legalContentRender.js) üzerinden ikinci bir savunma-derinliği
 * katmanından geçirip gösterir.
 *
 * ⛔ GÜVENLİK — Düzenleme Modu canlı önizlemesi: `renderMarkdownPreview`
 * (AdminLegalContent.jsx'e özgü, frontend'de markdown->HTML pipeline'ı)
 * KASITLI OLARAK KULLANILMAZ (import bile edilmez). Gerekçe TEK doğruluk
 * kaynağıdır: GEO içeriği backend'in `render_article_html` fonksiyonuyla
 * üretilip saklanır; frontend'de ikinci bir markdown->HTML yolu kurmak iki
 * ayrı parse/sanitize hattı demek olurdu.
 * (Not: legalContentRender 2026-09-10'dan beri tablo/blockquote/hr de
 * destekliyor — ama bu, yukarıdaki kararı değiştirmez.)
 * Onun yerine `POST /api/admin/geo/preview` (backend'in KENDİ render+bleach
 * pipeline'ı, PATCH .../content ile AYNI `render_article_html` fonksiyonu)
 * çağrılır; dönen `body_html` yine `sanitizeHtmlPreview` ile (savunma
 * derinliği, kaldırma amaçlı DEĞİL) süzülüp gösterilir. İki farklı
 * sanitize/parse yolu = güvenlik açığı; TEK doğruluk kaynağı korunur.
 *
 * Backend uçları:
 *   GET   /api/admin/geo/articles/{article_id}  — tüm alanlar, body_html dahil (require_admin)
 *   PATCH /api/content/{article_id}             — SADECE status + rejection_reason (require_admin)
 *   PATCH /api/content/{article_id}/content      — İÇERİK alanları (title/body_md/meta_description/
 *                                                   entities/sources/data_points/internal_links/
 *                                                   target_questions/pillar_id), kısmi güncelleme,
 *                                                   require_admin lane (X-API-Key header'ı YOK)
 *   POST  /api/admin/geo/preview                — stateless canlı önizleme, DB'ye dokunmaz (require_admin)
 *
 * Durum makinesi (backend routes/geo_content.py::_ALLOWED_TRANSITIONS):
 *   draft -> review -> published | rejected
 *   published -> archived
 * İçerik düzenleme `archived` HARİÇ her durumda mümkün (backend 403
 * `article_archived_not_editable`) — Düzenle butonu `rejected`/`archived`'da
 * da HİÇ render edilmez (durum makinesinde terminal, düzenleme yanıltıcı
 * olur — bkz. proje-yoneticisi görev talimatı 2026-07-30).
 *
 * Route: /admin/geo-content/:articleId
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2,
  XCircle,
  Archive,
  ExternalLink,
  AlertTriangle,
  Hash,
  Link2,
  Database,
  HelpCircle,
  Tag,
  Loader2,
  Pencil,
  Save,
  Eye,
  EyeOff,
  X,
  Plus,
} from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  PageHeader,
  LoadingState,
  ConfirmProvider,
  useConfirmDialog,
} from '../components/admin';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../components/ui/collapsible';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
// ⛔ SADECE sanitizeHtmlPreview — renderMarkdownPreview BİLİNÇLİ OLARAK import
// edilmiyor (bkz. dosya başındaki güvenlik notu, hem Yayın Görünümü hem
// Düzenleme Modu canlı önizlemesi için geçerli).
import { sanitizeHtmlPreview } from '../utils/legalContentRender';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIVE_BASE_URL = 'https://kariyeronline.tr/makale';

const REJECT_REASON_MAX = 1000;

// ── Düzenlenebilir alan boyut sınırları — backend GeoContentUpdateRequest
// (routes/geo_content.py) ile BİREBİR AYNI, ayrı bir sınır İCAT EDİLMEDİ.
const TITLE_MAX = 300;
const META_DESCRIPTION_MAX = 500;
const BODY_MD_MAX = 200_000;
const DATA_POINTS_MAX = 30;

const PREVIEW_DEBOUNCE_MS = 500;

// Backend'de pillar_id sabit enum DEĞİL (models/geo.py) — AdminGeoContent.jsx
// (liste sayfası) filtre kısayoluyla AYNI bilinen iki değer.
const PILLAR_OPTIONS = [
  { value: 'cabin-crew', labelKey: 'admin.geoContent.pillar.cabinCrew' },
  { value: 'career', labelKey: 'admin.geoContent.pillar.career' },
];

const EDITABLE_STATUSES = ['draft', 'review', 'published'];

// ── Alan yardımcıları ─────────────────────────────────────────────────────

// "Satır başına bir öğe" textarea'larını backend'in beklediği string[]'e
// çevirir — JSON textarea KULLANILMIYOR (ui-ux spec kararı).
function linesToArray(str) {
  return String(str || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function arraysEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function normalizeDataPoints(list) {
  return (list || [])
    .map((dp) => ({
      label: (dp.label || '').trim(),
      value: (dp.value || '').trim(),
      source_url: (dp.source_url || '').trim(),
    }))
    .filter((dp) => dp.label || dp.value);
}

// article (GET yanıtı) -> düzenleme formu şekli. `form` state'i article'dan
// AYRI tutulur (bkz. ui-ux spec "Form state'i doğrudan article'a bağlama") —
// bu fonksiyon sadece Düzenleme Moduna girerken VE diff (isDirty/patch body)
// için bir "baseline" hesaplarken çağrılır, her render'da değil.
function articleToForm(article) {
  return {
    title: article.title || '',
    body_md: article.body_md || '',
    meta_description: article.meta_description || '',
    entities: (article.entities || []).join('\n'),
    target_questions: (article.target_questions || []).join('\n'),
    sources: (article.sources || []).join('\n'),
    internal_links: (article.internal_links || []).join('\n'),
    data_points: (article.data_points || []).map((dp) => ({
      label: dp.label || '',
      value: dp.value || '',
      source_url: dp.source_url || '',
    })),
    pillar_id: article.pillar_id || '',
  };
}

// `form` (baseline'a göre) -> backend'e gönderilecek KISMİ patch body.
// Sadece DEĞİŞEN alanlar dahil edilir (backend "en az 1 alan zorunlu, yoksa
// 422 no_fields_to_update" — kısmi güncelleme sözleşmesiyle uyumlu).
function buildPatchBody(form, baseline) {
  const body = {};
  if (form.title !== baseline.title) body.title = form.title;
  if (form.body_md !== baseline.body_md) body.body_md = form.body_md;
  if (form.meta_description !== baseline.meta_description) {
    body.meta_description = form.meta_description;
  }
  if (form.pillar_id !== baseline.pillar_id) body.pillar_id = form.pillar_id;

  const entities = linesToArray(form.entities);
  if (!arraysEqual(entities, linesToArray(baseline.entities))) body.entities = entities;

  const targetQuestions = linesToArray(form.target_questions);
  if (!arraysEqual(targetQuestions, linesToArray(baseline.target_questions))) {
    body.target_questions = targetQuestions;
  }

  const sources = linesToArray(form.sources);
  if (!arraysEqual(sources, linesToArray(baseline.sources))) body.sources = sources;

  const internalLinks = linesToArray(form.internal_links);
  if (!arraysEqual(internalLinks, linesToArray(baseline.internal_links))) {
    body.internal_links = internalLinks;
  }

  const dataPoints = normalizeDataPoints(form.data_points);
  if (!arraysEqual(dataPoints, normalizeDataPoints(baseline.data_points))) {
    body.data_points = dataPoints.map((dp) =>
      dp.source_url ? dp : { label: dp.label, value: dp.value }
    );
  }

  return body;
}

// GEO "TL;DR" rozeti düzeltmesi (ui-ux görev talimatı 2026-07-30): "TL;DR"
// harfi harfine aranan bir metin DEĞİL, çünkü bir sıralama faktörü değil —
// AI arama motorlarının çektiği şey başlığın hemen altındaki DOĞRUDAN CEVAP
// paragrafı (dil/başlık metninden bağımsız). Eski `/^#{1,2}\s*TL;?DR/im`
// regex'i Türkçe başlıklarda yanlışlıkla "Yok" diyordu (bkz. eski `hasTldr`).
function hasDirectAnswerParagraph(bodyMd) {
  if (!bodyMd) return false;
  const lines = bodyMd.split('\n');
  let headingIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^#{1,2}\s+\S/.test(lines[i])) {
      headingIdx = i;
      break;
    }
  }
  if (headingIdx === -1) return false;
  const paraLines = [];
  for (let i = headingIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') {
      if (paraLines.length > 0) break;
      continue;
    }
    if (/^#{1,6}\s/.test(line)) break;
    if (/^\s*([-*+]|\d+[.)])\s/.test(line)) break;
    if (/^\s*\|/.test(line)) break;
    paraLines.push(line);
  }
  if (paraLines.length === 0) return false;
  const plain = paraLines
    .join(' ')
    .replace(/[*_`]/g, '')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .trim();
  return plain.length >= 40 && plain.length <= 600;
}

// ── Hata durumu — AdminLegalContent.jsx ErrorState deseninin kopyası ────────
function ErrorState({ msg, onRetry, t }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      <AlertTriangle size={32} className="text-rose-500" />
      <p className="font-bold text-rose-600">{msg}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition-colors min-h-[40px]"
        >
          {t('admin.geoContent.btn.retry')}
        </button>
      )}
    </div>
  );
}

// ── GEO kalite sinyal rozeti ─────────────────────────────────────────────────
function SignalBadge({ icon: Icon, label, value, tone }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white min-w-0">
      <Icon size={16} className={tone || 'text-[var(--text-muted)]'} />
      <div className="min-w-0">
        <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide truncate">
          {label}
        </p>
        <p className="text-sm font-bold text-[var(--text-main)] truncate">{value}</p>
      </div>
    </div>
  );
}

// ── Reddetme modalı ────────────────────────────────────────────────────────
function RejectDialog({ open, onOpenChange, onSubmit, submitting, t }) {
  const [reason, setReason] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const disabled = submitting || reason.trim().length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-2xl border-2 border-[var(--border-light)] max-w-md"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          textareaRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-bold text-[var(--text-main)]">
            {t('admin.geoContent.reject.title')}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--text-muted)]">
            {t('admin.geoContent.reject.desc')}
          </DialogDescription>
        </DialogHeader>

        <div>
          <label
            htmlFor="geo-reject-reason"
            className="text-xs font-semibold text-[var(--text-muted)] block mb-1"
          >
            {t('admin.geoContent.reject.label')}
          </label>
          <Textarea
            id="geo-reject-reason"
            ref={textareaRef}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, REJECT_REASON_MAX))}
            placeholder={t('admin.geoContent.reject.placeholder')}
            maxLength={REJECT_REASON_MAX}
            rows={4}
            className="w-full rounded-xl border-2 border-[var(--border-light)] text-sm"
          />
          <p className="text-[11px] text-[var(--text-muted)] mt-1 text-right">
            {reason.length}/{REJECT_REASON_MAX}
          </p>
        </div>

        <DialogFooter className="gap-2">
          <button
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="px-4 py-2 rounded-xl border-2 border-[var(--border-heavy)] font-bold text-sm min-h-[40px] disabled:opacity-40"
          >
            {t('admin.btn_cancel')}
          </button>
          <button
            onClick={() => onSubmit(reason.trim())}
            disabled={disabled}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--brand-danger)] text-white font-bold text-sm min-h-[40px] disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
            {t('admin.geoContent.reject.confirm')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Meta yan panel — view modunda salt-okunur, edit modunda editable ───────
function MetaPanel({ article, t, editMode, form, onFieldChange }) {
  const [techOpen, setTechOpen] = useState(false);

  // ── Düzenleme Modu ─────────────────────────────────────────────────────
  if (editMode && form) {
    const entitiesArr = linesToArray(form.entities);
    const targetQuestionsArr = linesToArray(form.target_questions);
    const sourcesArr = linesToArray(form.sources);
    const internalLinksArr = linesToArray(form.internal_links);
    const dataPoints = form.data_points || [];

    const updateDataPoint = (index, patch) => {
      onFieldChange(
        'data_points',
        dataPoints.map((dp, i) => (i === index ? { ...dp, ...patch } : dp))
      );
    };
    const removeDataPoint = (index) => {
      onFieldChange('data_points', dataPoints.filter((_, i) => i !== index));
    };
    const addDataPoint = () => {
      if (dataPoints.length >= DATA_POINTS_MAX) return;
      onFieldChange('data_points', [...dataPoints, { label: '', value: '', source_url: '' }]);
    };

    const techRows = [
      [t('admin.geoContent.meta.authorName'), article.author_name],
      [t('admin.geoContent.meta.subtopic'), article.subtopic],
      [t('admin.geoContent.meta.topicId'), article.topic_id],
      [t('admin.geoContent.col.source'), article.source_service],
      [t('admin.geoContent.meta.idempotencyKey'), article.idempotency_key],
      [t('admin.geoContent.col.slug'), article.slug],
      [t('admin.geoContent.col.status'), t(`admin.geoContent.status.${article.status}`)],
      [t('admin.geoContent.meta.contentHash'), article.content_hash],
    ];

    return (
      <div className="bg-white border-2 border-[var(--border-light)] rounded-2xl p-4 space-y-4">
        <div>
          <label
            htmlFor="geo-edit-pillar"
            className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5 block"
          >
            {t('admin.geoContent.field.pillar')}
          </label>
          <Select value={form.pillar_id} onValueChange={(v) => onFieldChange('pillar_id', v)}>
            <SelectTrigger
              id="geo-edit-pillar"
              className="rounded-xl border-2 border-[var(--border-light)] text-sm"
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {PILLAR_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label
            htmlFor="geo-edit-target-questions"
            className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5 block"
          >
            {t('admin.geoContent.field.targetQuestions')}
          </label>
          <Textarea
            id="geo-edit-target-questions"
            rows={4}
            value={form.target_questions}
            onChange={(e) => onFieldChange('target_questions', e.target.value)}
            className="w-full rounded-xl border-2 border-[var(--border-light)] text-xs font-mono resize-y"
          />
          {targetQuestionsArr.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {targetQuestionsArr.map((q, i) => (
                <span
                  key={i}
                  className="inline-flex px-2 py-1 rounded-lg bg-[var(--bg-muted)] text-[var(--text-main)] text-[11px] font-semibold"
                >
                  {q}
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="geo-edit-sources"
            className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5 block"
          >
            {t('admin.geoContent.field.sources')}
          </label>
          <Textarea
            id="geo-edit-sources"
            rows={4}
            value={form.sources}
            onChange={(e) => onFieldChange('sources', e.target.value)}
            className="w-full rounded-xl border-2 border-[var(--border-light)] text-xs font-mono resize-y"
          />
          {sourcesArr.length > 0 && (
            <ul className="space-y-1 mt-2">
              {sourcesArr.map((src, i) => (
                <li key={i} className="text-[11px] text-[var(--brand-primary)] break-all">
                  {src}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label
            htmlFor="geo-edit-internal-links"
            className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5 block"
          >
            {t('admin.geoContent.field.internalLinks')}
          </label>
          <Textarea
            id="geo-edit-internal-links"
            rows={4}
            value={form.internal_links}
            onChange={(e) => onFieldChange('internal_links', e.target.value)}
            className="w-full rounded-xl border-2 border-[var(--border-light)] text-xs font-mono resize-y"
          />
          {internalLinksArr.length > 0 && (
            <ul className="space-y-1 mt-2">
              {internalLinksArr.map((link, i) => (
                <li key={i} className="text-[11px] text-[var(--text-main)] break-all">
                  {link}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
            {t('admin.geoContent.field.dataPoints')}
          </p>
          <div className="space-y-2">
            {dataPoints.map((dp, i) => (
              <div
                key={i}
                className="relative border border-[var(--border-light)] rounded-xl p-3 bg-[var(--bg-muted)] space-y-2"
              >
                <button
                  type="button"
                  onClick={() => removeDataPoint(i)}
                  aria-label={t('admin.geoContent.edit.dataPoints.removeAria').replace(
                    '{label}',
                    dp.label || `#${i + 1}`
                  )}
                  className="absolute top-2 right-2 text-rose-400 hover:text-rose-600 transition-colors"
                >
                  <X size={14} />
                </button>
                <div>
                  <label
                    htmlFor={`geo-dp-label-${i}`}
                    className="text-[10px] font-semibold text-[var(--text-muted)] block mb-0.5"
                  >
                    {t('admin.geoContent.field.dataPointLabel')}
                  </label>
                  <input
                    id={`geo-dp-label-${i}`}
                    value={dp.label}
                    onChange={(e) => updateDataPoint(i, { label: e.target.value })}
                    className="w-full px-2 py-1.5 pr-7 rounded-lg border border-[var(--border-light)] text-xs bg-white"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`geo-dp-value-${i}`}
                    className="text-[10px] font-semibold text-[var(--text-muted)] block mb-0.5"
                  >
                    {t('admin.geoContent.field.dataPointValue')}
                  </label>
                  <input
                    id={`geo-dp-value-${i}`}
                    value={dp.value}
                    onChange={(e) => updateDataPoint(i, { value: e.target.value })}
                    className="w-full px-2 py-1.5 rounded-lg border border-[var(--border-light)] text-xs bg-white"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`geo-dp-src-${i}`}
                    className="text-[10px] font-semibold text-[var(--text-muted)] block mb-0.5"
                  >
                    {t('admin.geoContent.field.dataPointSource')}
                  </label>
                  <input
                    id={`geo-dp-src-${i}`}
                    value={dp.source_url}
                    onChange={(e) => updateDataPoint(i, { source_url: e.target.value })}
                    className="w-full px-2 py-1.5 rounded-lg border border-[var(--border-light)] text-xs bg-white"
                  />
                </div>
              </div>
            ))}
            {dataPoints.length < DATA_POINTS_MAX && (
              <button
                type="button"
                onClick={addDataPoint}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-dashed border-[var(--border-light)] text-xs font-bold text-[var(--text-muted)] hover:text-[var(--brand-primary)] hover:border-[var(--brand-primary)] transition-colors"
              >
                <Plus size={14} />
                {t('admin.geoContent.btn.addDataPoint')}
              </button>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="geo-edit-entities"
            className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5 block"
          >
            {t('admin.geoContent.field.entities')}
          </label>
          <Textarea
            id="geo-edit-entities"
            rows={4}
            value={form.entities}
            onChange={(e) => onFieldChange('entities', e.target.value)}
            className="w-full rounded-xl border-2 border-[var(--border-light)] text-xs font-mono resize-y"
          />
          {entitiesArr.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {entitiesArr.map((e, i) => (
                <span
                  key={i}
                  className="inline-flex px-2 py-1 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] text-[11px] font-bold"
                >
                  {e}
                </span>
              ))}
            </div>
          )}
        </div>

        <Collapsible open={techOpen} onOpenChange={setTechOpen}>
          <CollapsibleTrigger className="text-xs font-bold text-[var(--text-muted)] hover:text-[var(--brand-primary)] transition-colors">
            {t('admin.geoContent.meta.technicalDetails')}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-1.5">
            {techRows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-2 text-[11px]">
                <span className="text-[var(--text-muted)] font-semibold">{label}</span>
                <span className="text-[var(--text-main)] text-right break-all">
                  {value || '—'}
                </span>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }

  // ── Yayın Görünümü (salt-okunur, mevcut davranış AYNEN korunur) ─────────
  const sources = article.sources || [];
  const dataPoints = article.data_points || [];
  const targetQuestions = article.target_questions || [];
  const entities = article.entities || [];

  return (
    <div className="bg-white border-2 border-[var(--border-light)] rounded-2xl p-4 space-y-4">
      <div>
        <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1">
          {t('admin.geoContent.meta.description')}
        </p>
        <p className="text-sm text-[var(--text-main)]">{article.meta_description || '—'}</p>
        <p className="text-[10px] text-[var(--text-muted)] mt-1">
          {(article.meta_description || '').length} {t('admin.geoContent.meta.chars')}
        </p>
      </div>

      <div>
        <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
          {t('admin.geoContent.meta.targetQuestions')}
        </p>
        {targetQuestions.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] italic">—</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {targetQuestions.map((q, i) => (
              <span
                key={i}
                className="inline-flex px-2 py-1 rounded-lg bg-[var(--bg-muted)] text-[var(--text-main)] text-[11px] font-semibold"
              >
                {q}
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
          {t('admin.geoContent.meta.sources')}
        </p>
        {sources.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] italic">—</p>
        ) : (
          <ul className="space-y-1">
            {sources.map((src, i) => (
              <li key={i}>
                <a
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-[var(--brand-primary)] hover:underline break-all"
                >
                  <ExternalLink size={12} className="flex-shrink-0" />
                  {src}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
          {t('admin.geoContent.meta.dataPoints')}
        </p>
        {dataPoints.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] italic">—</p>
        ) : (
          <ul className="space-y-1">
            {dataPoints.map((dp, i) => (
              <li key={i} className="text-[11px] text-[var(--text-main)]">
                <span className="font-bold">{dp.label}:</span> {dp.value}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
          {t('admin.geoContent.meta.entities')}
        </p>
        {entities.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] italic">—</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {entities.map((e, i) => (
              <span
                key={i}
                className="inline-flex px-2 py-1 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] text-[11px] font-bold"
              >
                {e}
              </span>
            ))}
          </div>
        )}
      </div>

      <Collapsible open={techOpen} onOpenChange={setTechOpen}>
        <CollapsibleTrigger className="text-xs font-bold text-[var(--text-muted)] hover:text-[var(--brand-primary)] transition-colors">
          {t('admin.geoContent.meta.technicalDetails')}
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-1.5">
          {[
            [t('admin.geoContent.meta.authorName'), article.author_name],
            [t('admin.geoContent.col.pillar'), article.pillar_id],
            [t('admin.geoContent.meta.subtopic'), article.subtopic],
            [t('admin.geoContent.meta.topicId'), article.topic_id],
            [t('admin.geoContent.col.source'), article.source_service],
            [t('admin.geoContent.meta.idempotencyKey'), article.idempotency_key],
            [t('admin.geoContent.col.slug'), article.slug],
            [t('admin.geoContent.col.status'), t(`admin.geoContent.status.${article.status}`)],
            [t('admin.geoContent.meta.contentHash'), article.content_hash],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between gap-2 text-[11px]">
              <span className="text-[var(--text-muted)] font-semibold">{label}</span>
              <span className="text-[var(--text-main)] text-right break-all">{value || '—'}</span>
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// ── Ana içerik (ConfirmProvider gerektirir) ─────────────────────────────────
function GeoContentDetailInner() {
  const { articleId } = useParams();
  const { t, lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const tr = (trText, enText) => (lang === 'tr' ? trText : enText);
  const toast = useToast();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('rendered');
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  // ── Düzenleme Modu state'i — `form` BİLİNÇLİ OLARAK `article`'dan AYRI
  // tutulur (ui-ux spec): her tuşta rozet/sinyal yeniden hesaplanmasın
  // (kaydetmeden sonucu göstermek yanıltıcı olurdu), sadece Kaydet
  // başarılı olduğunda `article` (ve ona bağlı `signals` useMemo'su)
  // yeniden hesaplanır.
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflictBanner, setConflictBanner] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUnavailable, setPreviewUnavailable] = useState(false);

  const titleInputRef = useRef(null);
  const previewAbortRef = useRef(null);

  const fetchArticle = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/geo/articles/${articleId}`, {
        headers: authHeaders(),
      });
      setArticle(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [articleId, authHeaders, lang]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  const signals = useMemo(() => {
    if (!article) return null;
    const bodyMd = article.body_md || '';
    return {
      h2Count: (bodyMd.match(/^##\s+/gm) || []).length,
      hasDirectAnswer: hasDirectAnswerParagraph(bodyMd),
      hasTable: /\n\|.+\|.+\n\|[-:| ]+\|/.test(bodyMd),
      sourcesCount: (article.sources || []).length,
      dataPointsCount: (article.data_points || []).length,
      targetQuestionsCount: (article.target_questions || []).length,
      entitiesCount: (article.entities || []).length,
    };
  }, [article]);

  const renderedHtml = useMemo(
    () => (article ? sanitizeHtmlPreview(article.body_html) : ''),
    [article]
  );

  // baseline — `form`'un diff'lendiği referans; `article` her değiştiğinde
  // (ilk yükleme, kaydet-sonrası refetch) yeniden hesaplanır.
  const baselineForm = useMemo(() => (article ? articleToForm(article) : null), [article]);

  const isDirty = useMemo(() => {
    if (!form || !baselineForm) return false;
    return JSON.stringify(form) !== JSON.stringify(baselineForm);
  }, [form, baselineForm]);

  // ── Durum geçişleri (mevcut, değişmedi) ─────────────────────────────────
  const patchStatus = useCallback(
    async (targetStatus, rejectionReason) => {
      setActionLoading(true);
      try {
        const body = { status: targetStatus };
        if (rejectionReason) body.rejection_reason = rejectionReason;
        const res = await axios.patch(`${API}/content/${articleId}`, body, {
          headers: authHeaders(),
        });
        setArticle((prev) => (prev ? { ...prev, ...res.data } : prev));
        const toastKeyMap = {
          published: 'admin.geoContent.toast.published',
          rejected: 'admin.geoContent.toast.rejected',
          archived: 'admin.geoContent.toast.archived',
        };
        toast.success(t(toastKeyMap[targetStatus] || 'admin.geoContent.toast.published'));
        return true;
      } catch (err) {
        const httpStatus = err?.response?.status;
        if (httpStatus === 409) {
          toast.error(t('admin.geoContent.toast.conflict'));
          fetchArticle();
        } else if (httpStatus === 429) {
          toast.error(t('admin.geoContent.toast.rateLimited'));
        } else {
          toast.error(getApiErrorMessage(err, lang));
        }
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [articleId, authHeaders, toast, t, lang, fetchArticle]
  );

  const handlePublishClick = async () => {
    const ok = await confirm({
      title: t('admin.geoContent.publishConfirm.title'),
      description: t('admin.geoContent.publishConfirm.desc').replace('{slug}', article.slug),
      variant: 'success',
      confirmLabel: t('admin.geoContent.btn.publish'),
    });
    if (ok) await patchStatus('published');
  };

  const handleArchiveClick = async () => {
    const ok = await confirm({
      title: t('admin.geoContent.archiveConfirm.title'),
      description: t('admin.geoContent.archiveConfirm.desc').replace('{slug}', article.slug),
      variant: 'warning',
      confirmLabel: t('admin.geoContent.btn.archive'),
    });
    if (ok) await patchStatus('archived');
  };

  const handleRejectSubmit = async (reason) => {
    if (!reason) return;
    const ok = await patchStatus('rejected', reason);
    if (ok) setRejectOpen(false);
  };

  // ── Düzenleme Modu — giriş/çıkış ─────────────────────────────────────────
  const handleEnterEdit = () => {
    setForm(articleToForm(article));
    setConflictBanner(false);
    setPreviewHtml('');
    setPreviewUnavailable(false);
    setShowPreview(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
    setEditMode(true);
  };

  useEffect(() => {
    if (editMode) titleInputRef.current?.focus();
  }, [editMode]);

  const confirmDiscardIfDirty = useCallback(async () => {
    if (!isDirty) return true;
    return confirm({
      title: t('admin.geoContent.unsavedConfirm.title'),
      description: t('admin.geoContent.unsavedConfirm.desc'),
      variant: 'warning',
      confirmLabel: t('admin.geoContent.unsavedConfirm.confirm'),
      cancelLabel: t('admin.geoContent.unsavedConfirm.cancel'),
    });
  }, [isDirty, confirm, t]);

  const handleCancelEdit = async () => {
    const ok = await confirmDiscardIfDirty();
    if (!ok) return;
    setEditMode(false);
    setForm(null);
    setConflictBanner(false);
  };

  // Tarayıcı kapatma/yenileme koruması — sadece editMode + isDirty iken.
  useEffect(() => {
    const handler = (e) => {
      if (!editMode || !isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editMode, isDirty]);

  // Breadcrumb navigasyon koruması — proje `BrowserRouter` kullanıyor
  // (App.js), `useBlocker` data-router gerektirdiği için ÇALIŞMAZ; bunun
  // yerine breadcrumb Link'lerine özel onClick ile `e.preventDefault()` +
  // `useConfirmDialog` onayı, onaylanırsa `navigate()`. BİLİNEN SINIR:
  // `AdminLayout` sol menüsü bu korumaya sahip DEĞİL (paylaşılan bileşeni
  // tek sayfa için değiştirmek kapsam taşması — proje-yoneticisi görev
  // talimatı, ui-ux spec).
  const guardedNavigate = useCallback(
    (to) => async (e) => {
      if (!editMode || !isDirty) return;
      e.preventDefault();
      const ok = await confirmDiscardIfDirty();
      if (ok) navigate(to);
    },
    [editMode, isDirty, confirmDiscardIfDirty, navigate]
  );

  const handleFieldChange = (key, value) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };

  // ── Canlı önizleme — 500ms debounce + AbortController, backend
  // `POST /api/admin/geo/preview` (frontend markdown->HTML YOK, bkz. dosya
  // başındaki güvenlik notu).
  useEffect(() => {
    if (!editMode || !form || !showPreview) return undefined;
    const bodyMd = form.body_md;
    const timer = setTimeout(() => {
      if (previewAbortRef.current) previewAbortRef.current.abort();
      const controller = new AbortController();
      previewAbortRef.current = controller;
      setPreviewLoading(true);
      axios
        .post(
          `${API}/admin/geo/preview`,
          { body_md: bodyMd },
          { headers: authHeaders(), signal: controller.signal }
        )
        .then((res) => {
          setPreviewHtml(sanitizeHtmlPreview(res.data.body_html));
          setPreviewUnavailable(false);
        })
        .catch((err) => {
          if (axios.isCancel(err)) return; // önceki istek iptal edildi, yarış yok
          // Son başarılı önizleme PANELDE KALIR (state temizlenmez) — sadece
          // uyarı banner'ı gösterilir (bkz. ui-ux spec "panel boşalmasın").
          setPreviewUnavailable(true);
        })
        .finally(() => {
          setPreviewLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      // Debounce ateşlenmeden önceki state değişimlerinde clearTimeout yeterli
      // (istek hiç başlamadı); ancak istek zaten uçmuşsa (controller atanmış)
      // unmount/edit modundan çıkış sırasında da iptal edilmeli — aksi halde
      // gereksiz bir `/api/admin/geo/preview` isteği arka planda tamamlanıp
      // artık var olmayan state'i güncellemeye çalışır (bkz. reviewer notu).
      if (previewAbortRef.current) {
        previewAbortRef.current.abort();
        previewAbortRef.current = null;
      }
    };
  }, [form, editMode, showPreview, authHeaders]);

  // ── Kaydet ────────────────────────────────────────────────────────────
  const doSave = async () => {
    if (!form || !baselineForm) return;
    const body = buildPatchBody(form, baselineForm);
    if (Object.keys(body).length === 0) return;

    setSaving(true);
    setConflictBanner(false);
    try {
      await axios.patch(`${API}/content/${articleId}/content`, body, {
        headers: authHeaders(),
      });
      // PATCH .../content yanıtı SADECE {article_id, slug, status,
      // content_hash, body_html_source_hash, updated_at, updated_fields[]}
      // döner — title/body_md/body_html gibi görüntülenen alanları İÇERMEZ
      // (bkz. backend routes/geo_content.py::update_content). `signals`
      // useMemo'sunun ve "Yayın Görünümü" sekmesinin (body_html) DOĞRU
      // içeriği göstermesi için kısmi yanıtı `article`'a merge etmek YETMEZ
      // — kanonik veriyi GET ile TAM olarak yeniden çekiyoruz (bilinçli
      // sapma: ui-ux spec'in "mevcut setArticle(prev=>({...prev,...res.data}))
      // deseni" önerisi PATCH /content (durum ucu) için geçerli, çünkü o uç
      // TAM güncel dokümanı döner; içerik ucu dönmüyor).
      const res = await axios.get(`${API}/admin/geo/articles/${articleId}`, {
        headers: authHeaders(),
      });
      setArticle(res.data);
      toast.success(t('admin.geoContent.toast.saved'));
      setEditMode(false);
      setForm(null);
    } catch (err) {
      const httpStatus = err?.response?.status;
      if (httpStatus === 409) {
        // Form state KAYBOLMAZ, otomatik refetch YAPILMAZ — kullanıcı elle
        // karar versin (durum-geçişi PATCH'inden FARKLI davranış, bkz.
        // ui-ux spec).
        setConflictBanner(true);
      } else if (httpStatus === 429) {
        toast.error(t('admin.geoContent.toast.rateLimited'));
      } else {
        toast.error(getApiErrorMessage(err, lang));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = async () => {
    if (!isDirty || saving) return;
    if (article.status === 'published') {
      const ok = await confirm({
        title: t('admin.geoContent.editPublishConfirm.title'),
        description: t('admin.geoContent.editPublishConfirm.desc'),
        variant: 'warning',
        confirmLabel: t('admin.geoContent.editPublishConfirm.confirm'),
      });
      if (!ok) return;
    }
    await doSave();
  };

  // ── Aksiyon matrisi — geçersiz geçiş butonu DOM'da HİÇ OLMAZ ────────────────
  const renderActions = () => {
    if (!article) return null;
    switch (article.status) {
      case 'draft':
        return (
          <p className="text-sm text-[var(--text-muted)] italic">
            {t('admin.geoContent.draftNote')}
          </p>
        );
      case 'review':
        return (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handlePublishClick}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold transition-colors disabled:opacity-40 min-h-[40px]"
            >
              {actionLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCircle2 size={16} />
              )}
              {t('admin.geoContent.btn.publish')}
            </button>
            <button
              onClick={() => setRejectOpen(true)}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-rose-500 text-rose-600 hover:bg-rose-50 text-sm font-bold transition-colors disabled:opacity-40 min-h-[40px]"
            >
              <XCircle size={16} />
              {t('admin.geoContent.btn.reject')}
            </button>
          </div>
        );
      case 'published':
        return (
          <button
            onClick={handleArchiveClick}
            disabled={actionLoading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-amber-500 text-amber-600 hover:bg-amber-50 text-sm font-bold transition-colors disabled:opacity-40 min-h-[40px]"
          >
            {actionLoading ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />}
            {t('admin.geoContent.btn.archive')}
          </button>
        );
      case 'rejected':
        return (
          <div className="space-y-2">
            <div className="p-3 rounded-xl border-2 border-[var(--border-light)] bg-[var(--bg-muted)]">
              <p className="text-xs font-bold text-[var(--text-muted)] mb-1">
                {t('admin.geoContent.reject.label')}
              </p>
              <p className="text-sm text-[var(--text-main)]">{article.rejection_reason || '—'}</p>
            </div>
            <p className="text-sm text-[var(--text-muted)] italic">
              {t('admin.geoContent.rejectedNote')}
            </p>
            <p className="text-sm text-[var(--text-muted)] italic">
              {t('admin.geoContent.editRejectedNote')}
            </p>
          </div>
        );
      case 'archived':
        return (
          <div className="space-y-2">
            <p className="text-sm text-[var(--text-muted)] italic">
              {t('admin.geoContent.archivedNote')}
            </p>
            <p className="text-sm text-[var(--text-muted)] italic">
              {t('admin.geoContent.editArchivedNote')}
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const pageTitle = article?.title || t('admin.geoContent.pageTitle');
  const canEdit = article ? EDITABLE_STATUSES.includes(article.status) : false;
  const metaLen = form ? form.meta_description.length : 0;
  const metaIdeal = metaLen >= 150 && metaLen <= 160;
  // Kök kırıntı rol bazlı (`utils/adminBreadcrumbs`); hedefi guardedNavigate'e
  // sabit '/admin' yerine kırıntının kendi href'inden veriyoruz — danışman
  // rolünde hedef '/admin/cockpit' oluyor.
  const rootCrumb = rootBreadcrumb(user?.role, tr);

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { ...rootCrumb, onClick: guardedNavigate(rootCrumb.href) },
          {
            label: t('admin.nav.geoContent'),
            href: '/admin/geo-content',
            onClick: guardedNavigate('/admin/geo-content'),
          },
          { label: pageTitle },
        ]}
        title={pageTitle}
        subtitle={
          article ? `${article.pillar_id} · ${t(`admin.geoContent.status.${article.status}`)}` : undefined
        }
      />

      {article?.status === 'published' && article?.slug && !editMode && (
        <a
          href={`${LIVE_BASE_URL}/${article.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--brand-primary)] hover:underline mb-4"
        >
          <ExternalLink size={14} />
          {t('admin.geoContent.btn.viewLive')}
        </a>
      )}

      {loading ? (
        <div className="space-y-4">
          <LoadingState variant="form" fields={6} />
          <Skeleton className="h-64 rounded-xl w-full" />
        </div>
      ) : error ? (
        <ErrorState msg={error} onRetry={fetchArticle} t={t} />
      ) : !article ? null : (
        <>
          {/* GEO kalite sinyalleri */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
            <SignalBadge
              icon={Hash}
              label={t('admin.geoContent.signal.h2')}
              value={signals.h2Count}
              tone="text-[var(--brand-primary)]"
            />
            <SignalBadge
              icon={signals.hasDirectAnswer ? CheckCircle2 : XCircle}
              label={t('admin.geoContent.signal.directAnswer')}
              value={
                signals.hasDirectAnswer
                  ? t('admin.geoContent.signal.tldrYes')
                  : t('admin.geoContent.signal.tldrNo')
              }
              tone={signals.hasDirectAnswer ? 'text-emerald-500' : 'text-rose-500'}
            />
            <SignalBadge
              icon={signals.hasTable ? CheckCircle2 : XCircle}
              label={t('admin.geoContent.signal.table')}
              value={
                signals.hasTable
                  ? t('admin.geoContent.signal.tldrYes')
                  : t('admin.geoContent.signal.tldrNo')
              }
              tone={signals.hasTable ? 'text-emerald-500' : 'text-rose-500'}
            />
            <SignalBadge
              icon={Link2}
              label={t('admin.geoContent.signal.sources')}
              value={signals.sourcesCount}
              tone="text-[var(--brand-primary)]"
            />
            <SignalBadge
              icon={Database}
              label={t('admin.geoContent.signal.dataPoints')}
              value={signals.dataPointsCount}
              tone="text-[var(--brand-primary)]"
            />
            <SignalBadge
              icon={HelpCircle}
              label={t('admin.geoContent.signal.targetQuestions')}
              value={signals.targetQuestionsCount}
              tone="text-[var(--brand-primary)]"
            />
            <SignalBadge
              icon={Tag}
              label={t('admin.geoContent.signal.entities')}
              value={signals.entitiesCount}
              tone="text-[var(--brand-primary)]"
            />
          </div>

          {/* Aksiyon matrisi + Düzenle butonu */}
          <div className="flex flex-wrap items-start gap-3 mb-4">
            <div className="flex-1 min-w-[240px] p-4 rounded-2xl border-2 border-[var(--border-light)] bg-white">
              {renderActions()}
            </div>
            {canEdit && !editMode && (
              <button
                onClick={handleEnterEdit}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 text-sm font-bold transition-colors min-h-[40px]"
              >
                <Pencil size={16} />
                {t('admin.geoContent.btn.edit')}
              </button>
            )}
          </div>

          {editMode && form ? (
            // ── DÜZENLEME PANELİ ─────────────────────────────────────────
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-display text-base font-bold text-[var(--text-main)]">
                  {t('admin.geoContent.btn.edit')}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowPreview((v) => !v)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold text-[var(--text-main)] hover:bg-[var(--bg-muted)] transition-colors"
                  >
                    {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
                    {showPreview
                      ? t('admin.geoContent.edit.previewToggleHide')
                      : t('admin.geoContent.edit.previewToggleShow')}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl border-2 border-[var(--border-heavy)] font-bold text-xs min-h-[40px] disabled:opacity-40"
                  >
                    {t('admin.geoContent.btn.cancelEdit')}
                  </button>
                  <button
                    onClick={handleSaveClick}
                    disabled={!isDirty || saving}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-xs font-bold min-h-[40px] disabled:opacity-40 hover:opacity-90 transition-opacity"
                  >
                    {saving ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                    {saving ? t('admin.geoContent.saving') : t('admin.geoContent.btn.save')}
                  </button>
                </div>
              </div>

              {article.status === 'published' && (
                <div className="flex items-start gap-2 p-3 rounded-xl border-2 border-amber-500 bg-amber-50">
                  <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    {t('admin.geoContent.edit.publishedWarning').replace('{slug}', article.slug)}
                  </p>
                </div>
              )}

              {conflictBanner && (
                <div className="flex items-start gap-2 p-3 rounded-xl border-2 border-amber-500 bg-amber-50">
                  <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    {t('admin.geoContent.editConflict.banner')}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-4">
                  {/* title + meta_description — tam genişlik */}
                  <div className="bg-white border-2 border-[var(--border-light)] rounded-2xl p-4 space-y-3">
                    <div>
                      <label
                        htmlFor="geo-edit-title"
                        className="text-xs font-semibold text-[var(--text-muted)] block mb-1"
                      >
                        {t('admin.geoContent.field.title')}
                      </label>
                      <input
                        id="geo-edit-title"
                        ref={titleInputRef}
                        value={form.title}
                        onChange={(e) =>
                          handleFieldChange('title', e.target.value.slice(0, TITLE_MAX))
                        }
                        maxLength={TITLE_MAX}
                        aria-describedby="geo-edit-title-count"
                        className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                      />
                      <span
                        id="geo-edit-title-count"
                        className="text-[10px] text-[var(--text-muted)] mt-1 block text-right"
                      >
                        {form.title.length}/{TITLE_MAX}
                      </span>
                    </div>
                    <div>
                      <label
                        htmlFor="geo-edit-meta-description"
                        className="text-xs font-semibold text-[var(--text-muted)] block mb-1"
                      >
                        {t('admin.geoContent.field.metaDescription')}
                      </label>
                      <Textarea
                        id="geo-edit-meta-description"
                        rows={3}
                        value={form.meta_description}
                        onChange={(e) =>
                          handleFieldChange(
                            'meta_description',
                            e.target.value.slice(0, META_DESCRIPTION_MAX)
                          )
                        }
                        maxLength={META_DESCRIPTION_MAX}
                        aria-describedby="geo-edit-meta-description-count"
                        className="w-full rounded-xl border-2 border-[var(--border-light)] text-sm resize-y"
                      />
                      <div className="flex items-center justify-between mt-1">
                        <span
                          id="geo-edit-meta-description-count"
                          className={`text-[10px] flex items-center gap-1 ${
                            metaIdeal
                              ? 'text-emerald-600 font-semibold'
                              : 'text-[var(--text-muted)]'
                          }`}
                        >
                          {metaIdeal && <CheckCircle2 size={12} />}
                          {metaLen}/{META_DESCRIPTION_MAX}
                        </span>
                        <span className="text-[10px] text-[var(--text-muted)] text-right">
                          {t('admin.geoContent.field.metaDescriptionHint')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* body_md split view: textarea + canlı önizleme */}
                  <div
                    className={showPreview ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : ''}
                  >
                    <div>
                      <label
                        htmlFor="geo-edit-body-md"
                        className="text-xs font-semibold text-[var(--text-muted)] block mb-1"
                      >
                        {t('admin.geoContent.field.bodyMd')}
                      </label>
                      <Textarea
                        id="geo-edit-body-md"
                        rows={24}
                        value={form.body_md}
                        onChange={(e) =>
                          handleFieldChange('body_md', e.target.value.slice(0, BODY_MD_MAX))
                        }
                        maxLength={BODY_MD_MAX}
                        aria-describedby="geo-edit-body-md-count"
                        className="w-full rounded-xl border-2 border-[var(--border-light)] text-xs font-mono resize-y"
                      />
                      <span
                        id="geo-edit-body-md-count"
                        className="text-[10px] text-[var(--text-muted)] mt-1 block text-right"
                      >
                        {form.body_md.length}/{BODY_MD_MAX}
                      </span>
                    </div>

                    {showPreview && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-semibold text-[var(--text-muted)]">
                            {t('admin.geoContent.tab.rendered')}
                          </p>
                          {previewLoading && (
                            <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                              <Loader2 size={12} className="animate-spin" />
                              {t('admin.geoContent.edit.previewUpdating')}
                            </span>
                          )}
                        </div>
                        {previewUnavailable && (
                          <div className="flex items-start gap-2 p-2 rounded-lg border border-amber-300 bg-amber-50 mb-2">
                            <AlertTriangle
                              size={14}
                              className="text-amber-600 flex-shrink-0 mt-0.5"
                            />
                            <p className="text-xs text-amber-800">
                              {t('admin.geoContent.edit.previewUnavailable')}
                            </p>
                          </div>
                        )}
                        <div
                          className={`legal-doc-content bg-white border-2 border-[var(--border-light)] rounded-xl p-4 overflow-y-auto transition-opacity ${
                            previewLoading ? 'opacity-50' : 'opacity-100'
                          }`}
                          style={{ minHeight: 300, maxHeight: '60vh' }}
                          dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-1">
                  <div className="sticky top-4">
                    <MetaPanel
                      article={article}
                      t={t}
                      editMode
                      form={form}
                      onFieldChange={handleFieldChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // ── YAYIN GÖRÜNÜMÜ / HAM MARKDOWN ────────────────────────────
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-3">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="rendered">{t('admin.geoContent.tab.rendered')}</TabsTrigger>
                    <TabsTrigger value="markdown">{t('admin.geoContent.tab.markdown')}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="rendered">
                    {/* ⛔ SADECE body_html (backend'de bleach ile sanitize edilmiş) +
                        sanitizeHtmlPreview savunma-derinliği katmanı. Markdown parse
                        YOK — bkz. dosya başındaki güvenlik notu. */}
                    <div
                      className="legal-doc-content bg-white border-2 border-[var(--border-light)] rounded-xl p-6 max-h-[70vh] overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: renderedHtml }}
                    />
                  </TabsContent>
                  <TabsContent value="markdown">
                    <pre className="bg-gray-900 text-green-400 text-xs p-4 rounded-xl overflow-auto max-h-[70vh] font-mono whitespace-pre-wrap">
                      {article.body_md}
                    </pre>
                  </TabsContent>
                </Tabs>
              </div>

              <div className="md:col-span-1">
                <div className="sticky top-4">
                  <MetaPanel article={article} t={t} editMode={false} />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <RejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onSubmit={handleRejectSubmit}
        submitting={actionLoading}
        t={t}
      />
    </AdminLayout>
  );
}

// ── Public export (ConfirmProvider sarmalayıcı) ─────────────────────────────
export default function AdminGeoContentDetail() {
  return (
    <ConfirmProvider>
      <GeoContentDetailInner />
    </ConfirmProvider>
  );
}
